
import DateUtils from "@/utils/DateUtils.js";
import StringUtils from "../../../utils/StringUtils.js";
import RcDomainObject from "../../common/RcDomainObject.js";
import Company from "../company/Company.js";
import LabelMap from "../label/LabelMap.js";
import RoleMap from "../role/RoleMap.js";
import User from "../user/User.js";
import EmployeeMap from "./EmployeeMap.js";
import EmployeePreferences from "./preferences/EmployeePreferences.js";

export default class Employee extends RcDomainObject {

  static MODEL_NAME = "Employee";
  
  static FIELDS = {
    USER : "User",
    COMPANY : "Company",
    ROLES: RoleMap.MODEL_NAME,
    EMPLOYMENT_ID: 'employmentId',
    STATE: 'state',
    CREATED_DATE: 'createdDate',
    COMPLIANCE_TYPE: 'complianceType',
    START_DATE: 'startDate',
    END_DATE: 'endDate',
    PREFERENCES: 'EmployeePreferences',
    FIRST: "first",
    LAST: "last",
    LABELS: "LabelMap",
    CYCLE: "cycle",
  }
   
  static Values = {
      ComplianceType: {
        EXCLUDED: 'excluded',
        INCLUDED: 'included',
      }
  }
  constructor(domain, data) {
    super(domain, data, Employee.MODEL_NAME);
  }

  fields() {
    return Company.FIELDS;
  }

  copy() {
    var data = this._copy();
    return new Employee(this.domain, data);
  }

  find() {
    return this.domain.employees().findById(this.id());
  }
  
  user() {
    return new User(this.domain, this.data[Employee.FIELDS.USER]);
  }
  
  withUser(user) {
    this.put(Employee.FIELDS.USER, user);
    return this;
  }
  
  withPreferences(preferences) {
    this.put(Employee.FIELDS.PREFERENCES, preferences);
    return this;
  }

  roles() {
    return new RoleMap(this.domain, this.data[Employee.FIELDS.ROLES]);
  }
  
  preferences() {
    return new EmployeePreferences(this.domain, this.data[Employee.FIELDS.PREFERENCES]);
  }

  withRole(role) {
    let roleMap = new RoleMap(this.domain);
    roleMap.add(role);
    return this.withRoles(roleMap);
  }

  withRoles(roleMap) {
    this.put(Employee.FIELDS.ROLES, roleMap.data ? roleMap.data : roleMap);
    return this;
  }

  state() {
    return this.getString(Employee.FIELDS.STATE);
  }
  
  isStateInactive() {
    var is = this.state() === "inactive";
    return is;
  }
  
  isStateNotInvited() {
    var state = this.state();
    return state === 'notinvited';
  }

  isStateActive() {
    var is = this.state() === "active";
    return is;
  }
  
  isStateActiveOrInvited() {
    var is = this.isStateActive() || this.isStateInvited();
    return is;
  }
  
  isStateInvited() {
    var is = this.state() === "invited";
    return is;
  }
  
  employmentId() {
    return this.getString(Employee.FIELDS.EMPLOYMENT_ID);
  }
  
  withEmploymentId(id) {
    this.put(Employee.FIELDS.EMPLOYMENT_ID, id);
    return this;
  }

  createdDate() {
    var value = this.get(Employee.FIELDS.CREATED_DATE);
    return value;
  }

  startDate() {
    var value = this.getString(Employee.FIELDS.START_DATE);
    return value;
  }

  endDate() {
    var value = this.getString(Employee.FIELDS.END_DATE);
    return value;
  }

  createdDateDisplay() {
    var value = this.createdDate();
    return DateUtils.toDateTime(value);
  }
  
  company() {
    var companyData = this.data[Employee.FIELDS.COMPANY];
    return new Company(this.domain, companyData);
  }
  
  withCompany(company) {
    this.put(Employee.FIELDS.COMPANY, company);
    return this;
  }
  
  hasBeenExcluded() {
    var type = this.getString(Employee.FIELDS.COMPLIANCE_TYPE);
    return type === Employee.Values.ComplianceType.EXCLUDED;
  }
 
  hasBeenIncluded() {
    var type = this.getString(Employee.FIELDS.COMPLIANCE_TYPE);
    return type === Employee.Values.ComplianceType.INCLUDED;
  }
 
  complianceType() {
    var type = this.getString(Employee.FIELDS.COMPLIANCE_TYPE);
    return type;
  }
  
  withComplianceTypeIncluded() {
    this.put(Employee.FIELDS.COMPLIANCE_TYPE, Employee.Values.ComplianceType.INCLUDED)
    return this;
  }
  
  withComplianceTypeExcluded() {
    this.put(Employee.FIELDS.COMPLIANCE_TYPE, Employee.Values.ComplianceType.EXCLUDED)
    return this;
  }
  
  map() {
    var map = new EmployeeMap(this.domain);
    map.add(this);
    return map;
  }

  modifiedBy() {
    return new User(this.domain, this.get(Employee.DefaultFields.MODIFIED_BY));
  }

  createdBy() {
    return new User(this.domain, this.get(Employee.DefaultFields.CREATED_BY));
  }

  first() {
    if (this.isSystem()) {
      return "ReadyChek";
    }

    return this.getString(Employee.FIELDS.FIRST);
  }

  last() {
    if (this.isSystem()) {
      return "System";
    }

    return this.getString(Employee.FIELDS.LAST);
  }

  name() {
    return this.first();
  }
  
  fullName() {
    return StringUtils.format("{0} {1}", [this.first(), this.last()]);
  }

  email() {
    return this.user().find().email();
  }

  withLabels(labelMap) {
    this.put(Employee.FIELDS.LABELS, labelMap);
    return this;
  }

  labels() {
    const value = this.get(Employee.FIELDS.LABELS);
    return new LabelMap(this.domain, value);
  }

  pointer() {
    var e = new Employee(this.domain);
    e.withId(this.id());
    return e;
  }

  cycle() {
    return this.getInt(Employee.FIELDS.CYCLE);
  }

  cycleDisplay() {
    const cycle = this.cycle();
    if (cycle == 1) {
      return "1 (7 Days)";
    } else if (cycle == 2) {
      return "2 (14 Days)";
    }
    return "1 (7 Days)";
  }
}